import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2 } from 'lucide-react';

const CreditPaywall = ({ 
  open, 
  onOpenChange, 
  requiredCredits = 1, 
  actionName = "this item",
  currentCount = null,
  freeLimit = null,
  onUseCredit = null
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { creditBalance, hasConfirmedBalance, refreshCreditBalance } = useAuth();
  const [usingCredit, setUsingCredit] = useState(false);
  
  // User has enough credits to proceed (only if we have confirmed balance)
  const canUseCredits = hasConfirmedBalance && creditBalance >= requiredCredits && onUseCredit;

  const handleUseCredit = async () => {
    if (!onUseCredit) return;
    
    setUsingCredit(true);
    try {
      await onUseCredit();
      await refreshCreditBalance();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to use credit:', error);
      await refreshCreditBalance();
    } finally {
      setUsingCredit(false);
    }
  };

  const goToEarnCredits = () => {
    onOpenChange(false);
    navigate('/credits');
  };

  const goToViewCredits = () => {
    onOpenChange(false);
    navigate('/credits');
  };

  const remainingCredits = creditBalance - requiredCredits;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="credit-paywall-modal">
        
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {t('paywall.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-5">
          
          {/* Body copy */}
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              {t('paywall.limitReached', { item: actionName })}
            </p>
            <p>
              {t('paywall.creditsHelp')}
            </p>
            <p>
              {t('paywall.earnOrView')}
            </p>
          </div>

          {/* Clarity line - This action uses 1 credit */}
          <div className="p-3 bg-muted/40 rounded-lg text-sm text-center">
            <span className="font-medium">This action uses 1 credit.</span>
          </div>

          {/* Current balance - if user has credits */}
          {hasConfirmedBalance && (
            <div className="flex items-center justify-between p-3 bg-muted/40 rounded-lg text-sm">
              <span className="text-muted-foreground">{t('paywall.yourBalance')}</span>
              <Badge variant="secondary" className="font-semibold">
                {creditBalance} {t('credits.balance')}
              </Badge>
            </div>
          )}

          {/* Use Credit Option - Only if user has enough credits */}
          {canUseCredits && (
            <div className="space-y-2">
              <Button 
                onClick={handleUseCredit}
                disabled={usingCredit}
                className="w-full"
                data-testid="use-credit-btn"
              >
                {usingCredit ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                {t('paywall.useCredit')}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                {t('paywall.remainingCredits', { count: remainingCredits })}
              </p>
            </div>
          )}

          {/* Two Clear Options */}
          <div className="space-y-3">
            {canUseCredits && (
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground">or</span>
                <div className="flex-1 h-px bg-border" />
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                onClick={goToEarnCredits}
                className="w-full"
                data-testid="earn-credits-btn"
              >
                {t('paywall.earnCredits')}
              </Button>
              <Button
                variant="outline"
                onClick={goToViewCredits}
                className="w-full"
                data-testid="view-credits-btn"
              >
                {t('paywall.viewCredits')}
              </Button>
            </div>
          </div>

          {/* Footer - CANONICAL EXPLANATION */}
          <p className="text-xs text-center text-muted-foreground pt-2">
            Credits are only for creating more. Everything you&apos;ve already made stays free.
          </p>
        </div>

      </DialogContent>
    </Dialog>
  );
};

export default CreditPaywall;
